import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Star, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/feedback/ToastHost";
import { SelectField } from "@/components/admin/fields";
import { errorMessage } from "@/lib/errors";
import { usePermissions } from "@/hooks/usePermissions";
import { Permission } from "@/lib/permissions";
import {
  listHaulierVehicleTypes,
  listVehicleTypes,
  replaceHaulierVehicleTypes,
  type HaulierVehicleTypeAssignment,
} from "@/services/api/loadPlans";

interface AssignmentDraft {
  vehicle_type_id: number;
  is_preferred: boolean;
  sort_order: number;
}

const toDraft = (r: HaulierVehicleTypeAssignment): AssignmentDraft => ({
  vehicle_type_id: r.vehicle_type_id,
  is_preferred: r.is_preferred,
  sort_order: r.sort_order,
});

/**
 * Ordered "default vehicle sizes" card for one haulier (Phase 11).
 *
 * Edits are saved immediately through the replace-assignment endpoint, so the
 * card can live under the haulier view *and* edit dialogs (like contacts).
 */
export function HaulierVehicleTypes({ haulierId }: { haulierId: number }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canRead = has(Permission.LOADPLANS_READ);

  const assigned = useQuery({
    queryKey: ["haulier-vehicle-types", haulierId],
    queryFn: () => listHaulierVehicleTypes(haulierId),
    enabled: haulierId > 0 && canRead,
  });
  const allTypes = useQuery({
    queryKey: ["vehicle-types", "all"],
    queryFn: () => listVehicleTypes(true),
    enabled: canRead,
  });

  const save = useMutation({
    mutationFn: (drafts: AssignmentDraft[]) =>
      replaceHaulierVehicleTypes(
        haulierId,
        drafts.map((d, index) => ({ ...d, sort_order: index })),
      ),
    onSuccess: () => {
      toast.success(t("admin.saved"));
      void qc.invalidateQueries({ queryKey: ["haulier-vehicle-types", haulierId] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!canRead) {
    return (
      <p className="text-sm text-muted-foreground">{t("admin.haulierVehicleTypesReadonly")}</p>
    );
  }

  const rows: HaulierVehicleTypeAssignment[] = assigned.data ?? [];
  const available = (allTypes.data ?? []).filter(
    (type) => !rows.some((row) => row.vehicle_type_id === type.id),
  );

  const persist = (drafts: AssignmentDraft[]) => {
    // Exactly one preferred type (the first when several were starred).
    const firstPreferred = drafts.findIndex((d) => d.is_preferred);
    save.mutate(
      drafts.map((d, index) => ({
        vehicle_type_id: d.vehicle_type_id,
        is_preferred: d.is_preferred && index === firstPreferred,
        sort_order: index,
      })),
    );
  };

  const add = (vehicleTypeId: string) => {
    if (!vehicleTypeId) return;
    persist([
      ...rows.map(toDraft),
      { vehicle_type_id: Number(vehicleTypeId), is_preferred: rows.length === 0, sort_order: 0 },
    ]);
  };

  const move = (index: number, delta: number) => {
    const drafts = rows.map(toDraft);
    const target = index + delta;
    if (target < 0 || target >= drafts.length) return;
    [drafts[index], drafts[target]] = [drafts[target], drafts[index]];
    persist(drafts);
  };

  const setPreferred = (index: number, preferred: boolean) => {
    persist(
      rows.map((r, i) => ({
        vehicle_type_id: r.vehicle_type_id,
        is_preferred: preferred ? i === index : false,
        sort_order: r.sort_order,
      })),
    );
  };

  const remove = (index: number) => {
    persist(rows.filter((_, i) => i !== index).map(toDraft));
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-semibold">{t("admin.haulierVehicleTypes")}</Label>
        <p className="text-xs text-muted-foreground">{t("admin.haulierVehicleTypesHint")}</p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("admin.noVehicleTypesAssigned")}</p>
      ) : (
        <div className="space-y-1">
          {rows.map((row, index) => (
            <div
              key={row.vehicle_type_id}
              className="flex items-center gap-2 rounded border px-2 py-1.5 text-sm"
            >
              <Checkbox
                checked={row.is_preferred}
                onCheckedChange={(v) => setPreferred(index, Boolean(v))}
                aria-label={t("admin.preferredVehicle")}
              />
              <Star
                className={
                  row.is_preferred
                    ? "h-4 w-4 fill-warning text-warning"
                    : "h-4 w-4 text-muted-foreground"
                }
              />
              <span className="min-w-0 flex-1 truncate">
                {row.vehicle_type.code} — {row.vehicle_type.name}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={index === 0 || save.isPending}
                onClick={() => move(index, -1)}
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={index === rows.length - 1 || save.isPending}
                onClick={() => move(index, 1)}
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={save.isPending}
                onClick={() => remove(index)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {available.length > 0 && (
        <SelectField
          label=""
          value=""
          onChange={add}
          options={[
            { value: "", label: t("admin.addVehicleType") },
            ...available.map((type) => ({
              value: type.id.toString(),
              label: `${type.code} — ${type.name}`,
            })),
          ]}
        />
      )}
    </div>
  );
}

